import type { RepeatUnit, TaskScheduleConditionalType } from '@/model/Task.model';

export const buildTags = (tags: string[]): number[] => {
  const processedTags = tags.reduce((uniqueValues: Set<number>, tag: string) => {
    tag.split(',').reduce((_, item) => {
      const trimmedItem = item.trim();
      if (trimmedItem !== '') {
        const numberValue = Number(trimmedItem);
        if (Number.isFinite(numberValue)) {
          uniqueValues.add(numberValue);
        }
      }
      return uniqueValues;
    }, uniqueValues);

    return uniqueValues;
  }, new Set<number>());

  return Array.from(processedTags);
};

export const RECURRING_PATTERN_MAX_DAYS = 365;

export interface RecurringPatternInputs {
  from: number | null;
  to: number | null;
  every: number | null;
  everyUnit: RepeatUnit;
  type: TaskScheduleConditionalType | undefined;
}

export const generateRecurringPattern = ({
  from,
  to,
  every,
  everyUnit,
  type,
}: RecurringPatternInputs): Array<number> => {
  if (from === null || to === null || every === null) {
    return [];
  }
  if (!Number.isInteger(from) || !Number.isInteger(to) || !Number.isInteger(every)) {
    return [];
  }
  if (every <= 0 || from > to) {
    return [];
  }

  const stepDays = everyUnit === 'weeks' ? every * 7 : every;
  const days: Array<number> = [];
  for (let day = from; day <= to; day += stepDays) {
    if (type === 'study_day' && day === 0) {
      continue;
    }
    days.push(day);
    if (days.length >= RECURRING_PATTERN_MAX_DAYS) {
      break;
    }
  }
  return days;
};

export const countRecurringPattern = ({ from, to, every, everyUnit, type }: RecurringPatternInputs): number => {
  if (from === null || to === null || every === null) {
    return 0;
  }
  if (!Number.isInteger(from) || !Number.isInteger(to) || !Number.isInteger(every)) {
    return 0;
  }
  if (every <= 0 || from > to) {
    return 0;
  }

  const stepDays = everyUnit === 'weeks' ? every * 7 : every;
  const stepCount = Math.floor((to - from) / stepDays) + 1;
  if (type === 'study_day') {
    const includesZero = from <= 0 && to >= 0 && (0 - from) % stepDays === 0;
    return includesZero ? stepCount - 1 : stepCount;
  }
  return stepCount;
};
